import { LoadingIndicator } from '@ansarada/ace-react';
import { Box, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import 'app/sass/styles.scss';
import 'focus-visible';

import TextField from '@mui/material/TextField';
import TranslationService from 'app/common/services/translation';
import {
  contentChallengeEmail,
  contentChallengePasscode,
  emailValidateIllustration,
  passcodeValidateIllustration,
} from '../../constant';
import { ChallengeType } from '../types';

interface Props {
  onSubmit: () => void;
  onChallengeInputChange: (challengeInput: string) => void;
  challengeType: ChallengeType;
  buttonState: 'submitting' | 'redirecting' | 'idle';
  errors?: string;
}

const ChallengeSecureSharing = (props: Props) => {
  const {
    onSubmit,
    onChallengeInputChange,
    challengeType,
    buttonState,
    errors,
  } = props;

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) =>
    onChallengeInputChange(e.target.value);

  const content =
    challengeType === ChallengeType.Email
      ? contentChallengeEmail
      : contentChallengePasscode;

  const illustration =
    challengeType === ChallengeType.Email
      ? emailValidateIllustration
      : passcodeValidateIllustration;

  const getSubmitButtonContent = () => {
    if (buttonState === 'submitting') {
      return <LoadingIndicator size={14} />;
    }
    if (buttonState === 'redirecting') {
      return (
        <>
          <LoadingIndicator size={14} />
          <Typography marginLeft={3}>
            {TranslationService.get('Redirecting')}
          </Typography>
        </>
      );
    }

    return <Typography>{content.submitBtnLabel}</Typography>;
  };

  return (
    <form onSubmit={onSubmit}>
      <Grid container spacing="40px" sx={{ padding: '64px 34px 64px 34px' }}>
        <Grid item xs={12} sm={3} md={4} lg={3} container direction="column">
          <Typography
            data-test-id="challenge-secure-sharing-heading"
            variant="h5"
            sx={{ marginBottom: '33px' }}
          >
            {content.heading}
          </Typography>
          <Typography
            data-test-id="challenge-secure-sharing-body1"
            variant="body1"
            sx={{ marginBottom: '33px' }}
          >
            {content.body1}
          </Typography>
          <Typography
            data-test-id="challenge-secure-sharing-body2"
            variant="body1"
            sx={{ marginBottom: '33px' }}
          >
            {content.body2}
          </Typography>
          <TextField
            autoFocus
            data-ansarada-ccd="true"
            data-test-id="challenge-secure-sharing-input"
            label={content.inputBtnLabel}
            variant="outlined"
            sx={{ marginBottom: '13px' }}
            onChange={handleChangeInput}
            type={challengeType === ChallengeType.Email ? 'email' : 'password'}
            error={!!errors}
            helperText={errors ?? ' '}
          />
          <Button
            data-test-id="challenge-secure-sharing-submit-button"
            color="primary"
            sx={{ width: 'fit-content' }}
            variant="contained"
            disabled={
              buttonState === 'submitting' || buttonState === 'redirecting'
            }
            type="submit"
          >
            {getSubmitButtonContent()}
          </Button>
        </Grid>
        <Grid item xs={8} sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Box
            component="img"
            maxWidth="375px"
            marginTop="8px"
            alt={`Validate ${challengeType}`}
            src={illustration}
          />
        </Grid>
      </Grid>
    </form>
  );
};

export default ChallengeSecureSharing;
